import { Emitter } from "../utils/Emitter";
import { config } from "../config";

export interface FrameInfo {
  /** Secondes écoulées depuis la frame précédente, plafonnées. */
  dt: number;
  /** Secondes écoulées depuis start(), temps de pause exclu. */
  elapsed: number;
}

export interface GameEvents extends Record<string, unknown> {
  tick: FrameInfo;
  visibility: { paused: boolean };
}

/**
 * Boucle de rendu et horloge. Ne sait pas ce qui est mis à jour :
 * elle émet `tick`, l'assemblage se fait dans main.ts.
 */
export class Game {
  readonly events = new Emitter<GameEvents>();

  private frameId = 0;
  private running = false;
  private lastTime = 0;
  private elapsed = 0;

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.pause();
    else this.resume();
  };

  constructor() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.frameId = requestAnimationFrame(this.loop);
  }

  pause(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.frameId);
    this.events.emit("visibility", { paused: true });
  }

  resume(): void {
    if (this.running) return;
    this.start();
    this.events.emit("visibility", { paused: false });
  }

  private readonly loop = (now: number): void => {
    if (!this.running) return;
    this.frameId = requestAnimationFrame(this.loop);
    const dt = Math.min((now - this.lastTime) / 1000, config.loop.maxDeltaTime);
    this.lastTime = now;
    this.elapsed += dt;
    this.events.emit("tick", { dt, elapsed: this.elapsed });
  };

  dispose(): void {
    this.running = false;
    cancelAnimationFrame(this.frameId);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.events.dispose();
  }
}
